import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Switch,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  listSavedSearches,
  renameSavedSearch,
  setSavedSearchAlerts,
  deleteSavedSearch,
  getSavedSearchMatches,
  setSavedSearchLastViewed,
  savedSearchFiltersToAdvancedSearchFilters,
  listPortfolios,
  SavedSearchFilters,
  SavedSearchDisplayFilters,
} from "../data/request";
import { getDateDisplay } from "../data/utilities";
import { DELTA_ACCENT, DELTA_PRIMARY, STATUS_GREEN, TEXT_SECONDARY } from "./colors";
import { CompanyStatus } from "./Components/CompanyStatus";
import { AddToPortfolioButton } from "./Components/AddToPortfolioButton";

// Only the first this many rows of a Dynamic Portfolio's match table show by
// default - long-running searches can accumulate SEED_MAX_MATCHES-worth of
// rows (see lambda_saved_searches.py), which makes every portfolio's card
// enormous. "Show all N" below the table reveals the rest.
const COLLAPSED_ROW_COUNT = 5;

type DynamicPortfolio = {
  search_id: string;
  name: string;
  filters: SavedSearchFilters;
  // Display-only date filter snapshot from whenever this search was saved -
  // never used for matching (see lambda_saved_searches.py's
  // _create_saved_search / lambda_monitor_portfolios.py's
  // _ch_advanced_search, both of which never read this). Absent on any
  // saved search that had no date filter active at save time.
  display_filters?: SavedSearchDisplayFilters;
  // ISO timestamp this saved search was created at - used alongside
  // display_filters.incorporated_after to detect a relative "Last N days"
  // window worth showing instead of the raw stored date (see
  // describeIncorporatedAfter below).
  created_at?: string;
  alerts_enabled?: boolean;
  // Only present when the one-time creation-time seed (see
  // lambda_saved_searches.py's _seed_saved_search) found more real matches
  // than got persisted/capped - the true total, for the "showing the most
  // recent N of X" caption below the match table.
  seed_total_hits?: number;
};

type SavedSearchMatch = {
  company_number: string;
  company_name: string;
  incorporation_date: string;
  company_status: string;
  matched_at: number;
};

type Portfolio = { portfolio_id: string; name: string };

// A match counts as "new" when it arrived after the search's last_viewed_at
// snapshot from before this page view - matched_at is epoch seconds
// (lambda_saved_searches.py's _seed_saved_search / lambda_monitor_portfolios.py
// both use int(time.time())), last_viewed_at is an ISO timestamp, hence the
// *1000. Absent last_viewed_at means "never viewed" - treat everything as new.
const isMatchNew = (match: SavedSearchMatch, lastViewedAtIso: string | undefined) => {
  if (!lastViewedAtIso) return true;
  return match.matched_at * 1000 > Date.parse(lastViewedAtIso);
};

type FilterPill = { key: string; label: string };

// Whole-day difference between a saved search's created_at (its calendar
// date, local time) and an ISO YYYY-MM-DD date string - used only to detect
// whether a stored "incorporated after" date lines up with a round "last N
// days" window someone picked via the Examples chips (see
// SearchResultPage.tsx's isoDaysAgo), so the card can show that instead of a
// bare date. Not used for anything else - matching itself never depends on
// this.
const daysBetween = (createdAtIso: string, isoDate: string) => {
  const created = new Date(createdAtIso);
  const createdMidnight = new Date(created.getFullYear(), created.getMonth(), created.getDate());
  const [year, month, day] = isoDate.split("-").map(Number);
  const targetMidnight = new Date(year, month - 1, day);
  return Math.round((createdMidnight.getTime() - targetMidnight.getTime()) / 86400000);
};

// "Last N days" when the stored date lines up exactly with created_at minus
// a whole number of days, otherwise falls back to the plain stored date -
// still fully correct/explicit, just less friendly to read.
const describeIncorporatedAfter = (incorporatedAfter: string, createdAt: string | undefined) => {
  if (createdAt) {
    const days = daysBetween(createdAt, incorporatedAfter);
    if (days > 0) return `Last ${days} days`;
  }
  return `after ${incorporatedAfter}`;
};

// One Chip pill per active filter (name/SIC/status/location, plus whatever
// date range was active at save time) rather than a single run-on sentence -
// order mirrors the filter bar on the search results page.
const summariseFilters = (
  filters: SavedSearchFilters,
  displayFilters: SavedSearchDisplayFilters | undefined,
  createdAt: string | undefined
): FilterPill[] => {
  const pills: FilterPill[] = [];
  if (filters.name) pills.push({ key: "name", label: `Name: "${filters.name}"` });
  if (filters.sic_code) pills.push({ key: "sic", label: `SIC: ${filters.sic_code}` });
  if (filters.status) pills.push({ key: "status", label: `Status: ${filters.status}` });
  if (filters.location) pills.push({ key: "location", label: `Location: ${filters.location}` });
  if (displayFilters?.incorporated_after) {
    pills.push({
      key: "after",
      label: `Date Incorporated: ${describeIncorporatedAfter(displayFilters.incorporated_after, createdAt)}`,
    });
  }
  if (displayFilters?.incorporated_before) {
    pills.push({ key: "before", label: `Date Incorporated: before ${displayFilters.incorporated_before}` });
  }
  return pills;
};

// Rendered inside the "Dynamic Portfolios" tab of PortfolioPage - the parent
// already confirmed the signed-in user is Enterprise before mounting this,
// matching how e.g. FinancialsPanel trusts CompanyDetailsPage's auth.isPro
// check rather than re-checking tier itself.
//
// highlightSearchId arrives from PortfolioPage.tsx's location.state, itself
// forwarded from SearchResultPage.tsx's "view it" link on an already-saved
// search - once loaded, that card gets scrolled into view and briefly
// flashed so it's obvious which one the user was pointed at.
export const DynamicPortfoliosPanel = ({ highlightSearchId }: { highlightSearchId?: string }) => {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState<DynamicPortfolio[]>([]);
  const [matches, setMatches] = useState<Record<string, SavedSearchMatch[]>>({});
  // The last_viewed_at each search had stored BEFORE this page view - captured
  // once when matches load and never overwritten afterwards, so the "New"
  // badge always diffs against what was true when the user arrived, not
  // against the last_viewed_at this same page view is about to write back.
  const [lastViewedAt, setLastViewedAt] = useState<Record<string, string | undefined>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [addTargets, setAddTargets] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [renamingId, setRenamingId] = useState<string>();
  const [renameValue, setRenameValue] = useState("");
  const [portfolioToDelete, setPortfolioToDelete] = useState<DynamicPortfolio>();
  const [alertsToggle, setAlertsToggle] = useState<DynamicPortfolio>();
  const [flashSearchId, setFlashSearchId] = useState<string>();
  // Guards against firing the mark-as-viewed PATCH more than once for the
  // same search (e.g. React 18 StrictMode's double-invoke in development, or
  // any other re-render once loading has already settled to false).
  const markedViewedRef = useRef<Set<string>>(new Set());

  const loadAll = async () => {
    setLoading(true);
    const doc = await listSavedSearches();
    const list: DynamicPortfolio[] = doc?.saved_searches ?? [];
    setPortfolios(list);

    // Named (non-default) portfolios a matched company can be filed into via
    // AddToPortfolioButton - fetched once here rather than per-row, same
    // reasoning CompanyActions.tsx fetches its own list once per mount.
    const portfoliosDoc = await listPortfolios();
    setAddTargets((portfoliosDoc?.portfolios ?? []).filter((p: Portfolio) => p.portfolio_id !== "default"));

    const matchesBySearch: Record<string, SavedSearchMatch[]> = {};
    const lastViewedBySearch: Record<string, string | undefined> = {};
    await Promise.all(
      list.map(async (p) => {
        const matchesDoc = await getSavedSearchMatches(p.search_id);
        matchesBySearch[p.search_id] = matchesDoc?.matches ?? [];
        // Captured here, BEFORE the mark-as-viewed effect below ever runs -
        // this is deliberately the "as it was before this page view" value
        // the New badge (see isMatchNew) diffs against.
        lastViewedBySearch[p.search_id] = matchesDoc?.last_viewed_at ?? undefined;
      })
    );
    setMatches(matchesBySearch);
    setLastViewedAt(lastViewedBySearch);
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Marks every loaded Dynamic Portfolio as viewed-as-of-now, but only AFTER
  // the data (and this render's "New" badges, computed from the
  // pre-this-view lastViewedAt captured above) has actually reached the
  // screen. Doesn't wait for any specific user interaction - firing once
  // per portfolio right after its matches are displayed is enough to
  // satisfy "seen it", same as e.g. a notification tray marking itself read
  // on open rather than requiring a click on each item.
  useEffect(() => {
    if (loading) return;
    const nowIso = new Date().toISOString();
    portfolios.forEach((p) => {
      if (markedViewedRef.current.has(p.search_id)) return;
      markedViewedRef.current.add(p.search_id);
      setSavedSearchLastViewed(p.search_id, nowIso);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, portfolios]);

  // Scroll the targeted card into view and flash it briefly, once loading
  // has actually finished (the card doesn't exist in the DOM before then).
  // block: "start" (not "center") - a card's match table can run to
  // SEED_MAX_MATCHES (100) rows and be much taller than the viewport, and
  // centering a card that tall leaves the viewport somewhere in the middle
  // of its table rather than showing the card's own name/header.
  useEffect(() => {
    if (loading || !highlightSearchId) return;
    const el = document.getElementById(`dynamic-portfolio-${highlightSearchId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "auto", block: "start" });
    setFlashSearchId(highlightSearchId);
    const timer = setTimeout(() => setFlashSearchId(undefined), 2000);
    return () => clearTimeout(timer);
  }, [loading, highlightSearchId]);

  if (loading) {
    return (
      <Box style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const handleStartRename = (p: DynamicPortfolio) => {
    setRenamingId(p.search_id);
    setRenameValue(p.name);
  };

  const handleConfirmRename = async () => {
    const name = renameValue.trim();
    if (!name || !renamingId) {
      setRenamingId(undefined);
      return;
    }
    const doc = await renameSavedSearch(renamingId, name);
    if (doc?.error) {
      setError(doc.error);
    } else {
      setPortfolios(portfolios.map((p) => (p.search_id === renamingId ? { ...p, name } : p)));
    }
    setRenamingId(undefined);
  };

  const handleConfirmDelete = async () => {
    if (!portfolioToDelete) return;
    await deleteSavedSearch(portfolioToDelete.search_id);
    setPortfolios(portfolios.filter((p) => p.search_id !== portfolioToDelete.search_id));
    setPortfolioToDelete(undefined);
  };

  const handleConfirmToggleAlerts = async () => {
    if (!alertsToggle) return;
    const p = alertsToggle;
    setAlertsToggle(undefined);
    const nextValue = !(p.alerts_enabled ?? true);
    setPortfolios(portfolios.map((x) => (x.search_id === p.search_id ? { ...x, alerts_enabled: nextValue } : x)));
    const doc = await setSavedSearchAlerts(p.search_id, nextValue);
    if (doc?.error) {
      setError(doc.error);
      setPortfolios(portfolios.map((x) => (x.search_id === p.search_id ? { ...x, alerts_enabled: !nextValue } : x)));
    }
  };

  // Internal navigation, not something that needs to be bookmarkable - the
  // filters travel via router state rather than URL params. The :searchText
  // route param itself is never read back out for this flow (SearchResultPage
  // prefers location.state.filters when present), so it only needs to be a
  // non-empty, readable slug.
  const handleRunAgain = (p: DynamicPortfolio) => {
    navigate(`/search/${encodeURIComponent(p.name || "dynamic-portfolio")}`, {
      state: { filters: savedSearchFiltersToAdvancedSearchFilters(p.filters ?? {}, p.display_filters) },
    });
  };

  return (
    <Box>
      {error && (
        <Typography variant="body2" style={{ color: "#C62828", marginBottom: 16 }}>
          {error}
        </Typography>
      )}

      {portfolios.length === 0 ? (
        <Paper elevation={0} style={{ padding: 20 }}>
          <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
            No Dynamic Portfolios yet. Head to the{" "}
            <RouterLink to="/" style={{ color: DELTA_ACCENT }}>
              search bar
            </RouterLink>{" "}
            to run an advanced search, then save it here as a Dynamic Portfolio.
          </Typography>
        </Paper>
      ) : (
        portfolios.map((p) => {
          const rawMatches = matches[p.search_id] ?? [];
          const searchLastViewedAt = lastViewedAt[p.search_id];
          // New-first, then everything else in the order the backend already
          // sorted it (most-recently-matched first) - Array.prototype.sort is
          // stable, so a straight "new beats not-new" comparator preserves
          // that existing relative order within each group.
          const sortedMatches = [...rawMatches].sort((a, b) => {
            const aNew = isMatchNew(a, searchLastViewedAt);
            const bNew = isMatchNew(b, searchLastViewedAt);
            return aNew === bNew ? 0 : aNew ? -1 : 1;
          });
          const isExpanded = expanded[p.search_id] ?? false;
          const visibleMatches = isExpanded ? sortedMatches : sortedMatches.slice(0, COLLAPSED_ROW_COUNT);

          return (
          <Paper
            key={p.search_id}
            id={`dynamic-portfolio-${p.search_id}`}
            elevation={0}
            style={{
              padding: 20,
              marginBottom: 16,
              backgroundColor: flashSearchId === p.search_id ? "#F3E5F5" : undefined,
              transition: "background-color 0.5s ease",
            }}
          >
            <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              {renamingId === p.search_id ? (
                <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <TextField
                    size="small"
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleConfirmRename();
                      if (e.key === "Escape") setRenamingId(undefined);
                    }}
                  />
                  <IconButton size="small" onClick={handleConfirmRename} style={{ color: DELTA_ACCENT }}>
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setRenamingId(undefined)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Typography variant="subtitle1" style={{ color: DELTA_PRIMARY, fontWeight: 700 }}>
                    {p.name}
                  </Typography>
                  <IconButton size="small" onClick={() => handleStartRename(p)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Tooltip title={(p.alerts_enabled ?? true) ? "Email alerts on" : "Email alerts off"}>
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <Switch size="small" checked={p.alerts_enabled ?? true} onChange={() => setAlertsToggle(p)} />
                    <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                      Email me when a new company matches
                    </Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Run this search again">
                  <IconButton size="small" onClick={() => handleRunAgain(p)}>
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => setPortfolioToDelete(p)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, marginBottom: 8 }}>
              {(() => {
                const pills = summariseFilters(p.filters ?? {}, p.display_filters, p.created_at);
                return pills.length > 0 ? (
                  pills.map((pill) => (
                    <Chip
                      key={pill.key}
                      label={pill.label}
                      size="small"
                      variant="outlined"
                      style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                    No filters
                  </Typography>
                );
              })()}
            </Box>

            {sortedMatches.length === 0 ? (
              <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                No companies have matched yet.
              </Typography>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Incorporated</TableCell>
                      <TableCell>Company Number</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleMatches.map((m) => (
                      <TableRow key={m.company_number}>
                        <TableCell>
                          <Box style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <RouterLink
                              to={`/company/${m.company_number}`}
                              style={{ color: DELTA_ACCENT, textDecoration: "none", fontWeight: 600 }}
                            >
                              {m.company_name || m.company_number}
                            </RouterLink>
                            {isMatchNew(m, searchLastViewedAt) && (
                              <Chip
                                label="New"
                                size="small"
                                style={{ backgroundColor: STATUS_GREEN, color: "#FFFFFF", height: 18, fontSize: 11 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <CompanyStatus status={m.company_status} />
                        </TableCell>
                        <TableCell>{getDateDisplay(m.incorporation_date)}</TableCell>
                        <TableCell>{m.company_number}</TableCell>
                        <TableCell align="right">
                          <AddToPortfolioButton
                            portfolios={addTargets}
                            companyNumber={m.company_number}
                            companyName={m.company_name}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {sortedMatches.length > COLLAPSED_ROW_COUNT && (
                  <Box style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                    <Button
                      size="small"
                      onClick={() => setExpanded({ ...expanded, [p.search_id]: !isExpanded })}
                      style={{ color: DELTA_ACCENT }}
                    >
                      {isExpanded ? "Show less" : `Show all ${sortedMatches.length}`}
                    </Button>
                  </Box>
                )}
              </>
            )}

            {/* Only present when the creation-time seed found more real
                matches than fit under SEED_MAX_MATCHES - mirrors the
                "+N more, refine your search" note in the daily digest email
                (see lambda_monitor_portfolios.py's _render_digest_html). */}
            {(p.seed_total_hits ?? 0) > sortedMatches.length && (
              <Typography variant="caption" style={{ color: TEXT_SECONDARY, display: "block", marginTop: 8 }}>
                Showing the most recent {sortedMatches.length} of {p.seed_total_hits} matches — narrow
                your filter to see the full list.
              </Typography>
            )}
          </Paper>
          );
        })
      )}

      <Dialog open={Boolean(portfolioToDelete)} onClose={() => setPortfolioToDelete(undefined)}>
        <DialogTitle>Delete "{portfolioToDelete?.name}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>This removes the Dynamic Portfolio and its email alerts. This can't be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPortfolioToDelete(undefined)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} style={{ color: "#C62828" }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(alertsToggle)} onClose={() => setAlertsToggle(undefined)}>
        <DialogTitle>{(alertsToggle?.alerts_enabled ?? true) ? "Turn off email alerts?" : "Turn on email alerts?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {(alertsToggle?.alerts_enabled ?? true)
              ? `You will stop receiving emails when a new company matches "${alertsToggle?.name}".`
              : `You will start receiving emails when a new company matches "${alertsToggle?.name}".`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertsToggle(undefined)}>Cancel</Button>
          <Button onClick={handleConfirmToggleAlerts} style={{ color: DELTA_ACCENT }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DynamicPortfoliosPanel;
